import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const BACKEND = 'https://alphabioai-backend.onrender.com';

const T = {
  bg:        '#060d1e',
  surface:   '#0d1a2d',
  surfaceHi: '#132238',
  border:    '#1e3a5f',
  borderHi:  '#2d5a8e',
  text:      '#f0f6ff',
  textSub:   '#8ba5c8',
  textMute:  '#4a6a8a',
  emerald:   '#10b981',
  blue:      '#3b82f6',
  green:     '#22c55e',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
  teal:      '#2dd4bf',
  gold:      '#fbbf24',
};

const getRiskColor  = (r) => r === 'Low' ? T.green  : r === 'High' ? T.red  : T.amber;
const getRiskBg     = (r) => r === 'Low' ? 'rgba(34,197,94,0.08)' : r === 'High' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)';
const getRiskScore  = (r) => r === 'Low' ? 2 : r === 'High' ? 4 : 3;

const getAppTypeColor = (appNum) => {
  if (!appNum) return { color: T.textSub, bg: 'rgba(138,138,149,0.08)', label: 'DRUG' };
  if (appNum.startsWith('BLA'))  return { color: T.purple, bg: 'rgba(167,139,250,0.08)', label: 'BIOLOGIC' };
  if (appNum.startsWith('NDA'))  return { color: T.blue,   bg: 'rgba(59,130,246,0.08)',  label: 'BRAND' };
  if (appNum.startsWith('ANDA')) return { color: T.teal,   bg: 'rgba(45,212,191,0.08)',  label: 'GENERIC' };
  return { color: T.textSub, bg: 'rgba(138,138,149,0.08)', label: 'DRUG' };
};

const getPhaseColor = (phase) => {
  if (!phase) return { color: T.textSub, bg: 'rgba(138,138,149,0.08)' };
  if (phase.includes('PHASE1') || phase.includes('PHASE_1')) return { color: T.amber,  bg: 'rgba(245,158,11,0.08)' };
  if (phase.includes('PHASE2') || phase.includes('PHASE_2')) return { color: T.blue,   bg: 'rgba(59,130,246,0.08)' };
  if (phase.includes('PHASE3') || phase.includes('PHASE_3')) return { color: T.purple, bg: 'rgba(167,139,250,0.08)' };
  if (phase.includes('PHASE4') || phase.includes('PHASE_4')) return { color: T.teal,   bg: 'rgba(45,212,191,0.08)' };
  return { color: T.textSub, bg: 'rgba(138,138,149,0.08)' };
};

const getStatusColor = (s) => {
  if (!s) return T.textSub;
  if (s === 'RECRUITING') return T.green;
  if (s === 'COMPLETED')  return T.blue;
  if (s === 'ACTIVE_NOT_RECRUITING') return T.amber;
  if (s === 'TERMINATED') return T.red;
  return T.textSub;
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmt$ = (n) => {
  if (!n) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.ceil((new Date(dateStr) - today) / (1000*60*60*24));
};

/* ─── SVG Icons ─── */
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <polyline points="15 18 9 12 15 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BuildingIcon = ({ color, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="1.6" fill="none"/>
    <path d="M9 22V12h6v10" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <rect x="7" y="7" width="3" height="3" rx="0.5" stroke={color} strokeWidth="1.4"/>
    <rect x="14" y="7" width="3" height="3" rx="0.5" stroke={color} strokeWidth="1.4"/>
  </svg>
);

const PipelineIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.6" fill="none"/>
    <path d="M9 12l2 2 4-4" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrialIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M9 3v11l-4 6h14l-4-6V3" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="9" y1="3" x2="15" y2="3" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <line x1="8" y1="14" x2="16" y2="14" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const TradeIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CalIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke={color} strokeWidth="1.6" fill="none"/>
    <line x1="16" y1="2" x2="16" y2="6" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <line x1="3" y1="10" x2="21" y2="10" stroke={color} strokeWidth="1.6"/>
    <circle cx="8" cy="15" r="1" fill={color}/>
    <circle cx="12" cy="15" r="1" fill={color}/>
  </svg>
);

const StockIcon = ({ color, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="17 6 23 6 23 12" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AIIcon = ({ color, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ─── Format AI Summary ─── */
const formatSummary = (summary) => {
  if (!summary) return null;
  const cleaned = summary.replace(/^#.*?\n/, '').replace(/\*\*/g, '').trim();
  const sections = [
    { key: 'SUMMARY', color: T.textSub,  label: 'Summary' },
    { key: 'RISK',    color: T.amber,    label: 'Risk'    },
    { key: 'INSIGHT', color: T.teal,     label: 'Insight' },
  ];
  const parts = {};
  sections.forEach(({ key }) => {
    const regex = new RegExp(`${key}:\\s*(.+?)(?=SUMMARY:|RISK:|INSIGHT:|$)`, 's');
    const match = cleaned.match(regex);
    if (match) parts[key] = match[1].trim();
  });
  if (Object.keys(parts).length === 0) return <span style={{ color: T.textSub, fontSize: 14 }}>{cleaned}</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sections.map(({ key, color, label }) =>
        parts[key] ? (
          <div key={key} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '12px 16px', backgroundColor: T.surfaceHi }}>
            <span style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: '1px', textTransform: 'uppercase', minWidth: 60, paddingTop: 2, flexShrink: 0 }}>{label}</span>
            <span style={{ color: '#d8e8ff', fontSize: 14, lineHeight: 1.7, flex: 1 }}>{parts[key]}</span>
          </div>
        ) : null
      )}
    </div>
  );
};

export default function CompanyProfile({ companyName, onBack, watchlist, userPlan }) {
  const [drugs,       setDrugs]       = useState([]);
  const [trials,      setTrials]      = useState([]);
  const [trades,      setTrades]      = useState([]);
  const [calendar,    setCalendar]    = useState([]);
  const [narrative,   setNarrative]   = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(true);
  const [loading,     setLoading]     = useState(true);
  const [activeTab,   setActiveTab]   = useState('overview');
  const [expandedCard,setExpandedCard]= useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const normalize = (str) => str?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';
  const companyNorm = normalize(companyName);

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);

      const [drugsRes, trialsRes, tradesRes, calRes] = await Promise.all([
        supabase.from('fda_drugs').select('*').order('created_at', { ascending: false }).limit(2000),
        supabase.from('clinical_trials').select('*').order('created_at', { ascending: false }).limit(2000),
        supabase.from('insider_trades').select('ticker,company_name,insider_name,insider_title,trade_type,total_value,trade_date').order('trade_date', { ascending: false }).limit(500),
        supabase.from('fda_calendar').select('*').order('decision_date', { ascending: true }),
      ]);

      const matchedDrugs = (drugsRes.data || []).filter(d => {
        const mfr = normalize(d.manufacturer);
        return mfr.includes(companyNorm) || companyNorm.includes(mfr.slice(0, 6));
      });
      const matchedTrials = (trialsRes.data || []).filter(t => {
        const co = normalize(t.company);
        return co.includes(companyNorm) || companyNorm.includes(co.slice(0, 6));
      });
      const matchedTrades = (tradesRes.data || []).filter(t => {
        const co = normalize(t.company_name);
        return co.includes(companyNorm) || companyNorm.includes(co.slice(0, 6));
      });
      const matchedCal = (calRes.data || []).filter(e => {
        const co = normalize(e.company);
        return co.includes(companyNorm) || companyNorm.includes(co.slice(0, 6));
      });

      setDrugs(matchedDrugs);
      setTrials(matchedTrials);
      setTrades(matchedTrades);
      setCalendar(matchedCal);
      setLoading(false);

      // Fetch AI narrative
      setNarrativeLoading(true);
      try {
        const res = await fetch(`${BACKEND}/api/company-profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ company_name: companyName }),
        });
        const data = await res.json();
        if (data.narrative) setNarrative(data.narrative);
      } catch (e) {
        console.error('Narrative fetch error:', e);
      } finally {
        setNarrativeLoading(false);
      }
    }
    fetchAll();
  }, [companyName, companyNorm]);

  const allItems    = [...drugs, ...trials];
  const highRisk    = allItems.filter(i => i.risk_level === 'High').length;
  const total       = allItems.length || 1;
  const riskRatio   = highRisk / total;
  const overallRisk = riskRatio > 0.4 ? 'High' : riskRatio > 0.2 ? 'Medium' : 'Low';
  const overallColor = getRiskColor(overallRisk);

  const drugRisks  = { low: drugs.filter(d => d.risk_level === 'Low').length, medium: drugs.filter(d => d.risk_level === 'Medium').length, high: drugs.filter(d => d.risk_level === 'High').length };
  const trialRisks = { low: trials.filter(t => t.risk_level === 'Low').length, medium: trials.filter(t => t.risk_level === 'Medium').length, high: trials.filter(t => t.risk_level === 'High').length };
  const totalLow   = drugRisks.low + trialRisks.low;
  const totalMed   = drugRisks.medium + trialRisks.medium;
  const totalHigh  = drugRisks.high + trialRisks.high;
  const pct        = (n) => `${(n / total * 100).toFixed(0)}%`;

  const renderDrugCard = (item, i) => {
    const cardKey    = `drug-${i}`;
    const isExpanded = expandedCard === cardKey;
    const riskColor  = getRiskColor(item.risk_level);
    const riskScore  = getRiskScore(item.risk_level);
    const appType    = getAppTypeColor(item.application_number);
    return (
      <div key={cardKey} onClick={() => setExpandedCard(isExpanded ? null : cardKey)}
        style={{ backgroundColor: T.surface, borderRadius: 10, border: `1px solid ${isExpanded ? T.borderHi : T.border}`, borderLeft: `3px solid ${riskColor}`, cursor: 'pointer', overflow: 'hidden', transition: 'all 0.15s', marginBottom: 8 }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHi; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = isExpanded ? T.borderHi : T.border; }}>
        <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ flexShrink: 0, width: 40, height: 40, backgroundColor: getRiskBg(item.risk_level), border: `1px solid ${riskColor}30`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: riskColor, lineHeight: 1 }}>{riskScore}</span>
            <span style={{ fontSize: 10, color: T.textMute, lineHeight: 1 }}>/ 5</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{item.drug_name}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: appType.color, backgroundColor: appType.bg, padding: '2px 8px', borderRadius: 4 }}>{appType.label}</span>
            </div>
            <span style={{ fontSize: 13, color: T.textMute }}>{item.application_number}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: riskColor, backgroundColor: getRiskBg(item.risk_level), padding: '4px 10px', borderRadius: 6 }}>{item.risk_level}</span>
            <span style={{ fontSize: 12, color: T.textMute }}>{isExpanded ? '▴' : '▾'}</span>
          </div>
        </div>
        {isExpanded && (
          <div onClick={e => e.stopPropagation()}>
            <div style={{ padding: '12px 20px 8px', display: 'flex', alignItems: 'center', gap: 8, backgroundColor: T.surfaceHi }}>
              <AIIcon color={T.emerald} size={14}/>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.emerald, letterSpacing: '1px' }}>AI ANALYSIS</span>
            </div>
            {formatSummary(item.ai_summary)}
            <div style={{ padding: '10px 20px' }}><span style={{ fontSize: 12, color: T.textMute }}>ML risk score v2 — coming soon</span></div>
          </div>
        )}
      </div>
    );
  };

  const renderTrialCard = (item, i) => {
    const cardKey    = `trial-${i}`;
    const isExpanded = expandedCard === cardKey;
    const riskColor  = getRiskColor(item.risk_level);
    const riskScore  = getRiskScore(item.risk_level);
    const phaseColor = getPhaseColor(item.phase);
    const statusColor= getStatusColor(item.status);
    return (
      <div key={cardKey} onClick={() => setExpandedCard(isExpanded ? null : cardKey)}
        style={{ backgroundColor: T.surface, borderRadius: 10, border: `1px solid ${isExpanded ? T.borderHi : T.border}`, borderLeft: `3px solid ${riskColor}`, cursor: 'pointer', overflow: 'hidden', transition: 'all 0.15s', marginBottom: 8 }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHi; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = isExpanded ? T.borderHi : T.border; }}>
        <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ flexShrink: 0, width: 40, height: 40, backgroundColor: getRiskBg(item.risk_level), border: `1px solid ${riskColor}30`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: riskColor, lineHeight: 1 }}>{riskScore}</span>
            <span style={{ fontSize: 10, color: T.textMute, lineHeight: 1 }}>/ 5</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isMobile ? 200 : 500 }}>{item.title}</span>
              {item.phase && item.phase !== "['Unknown']" && (
                <span style={{ fontSize: 11, fontWeight: 700, color: phaseColor.color, backgroundColor: phaseColor.bg, padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}>{item.phase.replace(/[[\]']/g, '').trim()}</span>
              )}
            </div>
            {item.status && (
              <span style={{ fontSize: 13, color: statusColor, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 5, height: 5, backgroundColor: statusColor, borderRadius: '50%', display: 'inline-block' }}/>
                {item.status.replace(/_/g, ' ')}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: riskColor, backgroundColor: getRiskBg(item.risk_level), padding: '4px 10px', borderRadius: 6 }}>{item.risk_level}</span>
            <span style={{ fontSize: 12, color: T.textMute }}>{isExpanded ? '▴' : '▾'}</span>
          </div>
        </div>
        {isExpanded && (
          <div onClick={e => e.stopPropagation()}>
            <div style={{ padding: '12px 20px 8px', display: 'flex', alignItems: 'center', gap: 8, backgroundColor: T.surfaceHi }}>
              <AIIcon color={T.emerald} size={14}/>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.emerald, letterSpacing: '1px' }}>AI ANALYSIS</span>
            </div>
            {formatSummary(item.ai_summary)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: T.bg, color: T.text, fontFamily: "'Inter','DM Sans',system-ui,sans-serif" }}>

      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 50, backgroundColor: `${T.bg}f5`, borderBottom: `1px solid ${T.border}`, backdropFilter: 'blur(16px)', padding: isMobile ? '0 16px' : '0 40px', height: 60, display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={onBack}
          style={{ backgroundColor: 'transparent', border: `1px solid ${T.border}`, color: T.textSub, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s', flexShrink: 0 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHi; e.currentTarget.style.color = T.text; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSub; }}>
          <BackIcon /> Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, background: `linear-gradient(135deg,${T.blue},#1d4ed8)`, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <BuildingIcon color="#fff" size={18}/>
          </div>
          <div>
            <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 800, color: T.text, letterSpacing: '-0.3px', lineHeight: 1 }}>{companyName}</div>
            <div style={{ fontSize: 11, color: T.textMute, marginTop: 2 }}>Company Intelligence Profile</div>
          </div>
        </div>
      </div>

      <div style={{ padding: isMobile ? '20px 16px 48px' : '32px 40px 64px', maxWidth: 1100, margin: '0 auto' }}>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: T.textMute }}>
            <div style={{ fontSize: 15, marginBottom: 12 }}>Loading company intelligence...</div>
            <div style={{ width: 80, height: 2, backgroundColor: T.border, borderRadius: 1, overflow: 'hidden', margin: '0 auto' }}>
              <div style={{ height: '100%', width: '50%', backgroundColor: T.emerald, animation: 'loadBar 1.4s ease-in-out infinite' }}/>
            </div>
          </div>
        ) : (
          <>
            {/* Hero */}
            <div style={{ backgroundColor: T.surface, borderRadius: 14, border: `1px solid ${T.border}`, padding: isMobile ? '20px 18px' : '28px 32px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                  <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.text, letterSpacing: '-0.5px' }}>{companyName}</h1>
                  <span style={{ fontSize: 13, fontWeight: 700, color: overallColor, backgroundColor: getRiskBg(overallRisk), padding: '4px 14px', borderRadius: 100, border: `1px solid ${overallColor}30` }}>
                    {overallRisk} Risk Company
                  </span>
                </div>
                <p style={{ margin: 0, color: T.textSub, fontSize: 15 }}>
                  {drugs.length} FDA drug{drugs.length !== 1 ? 's' : ''} · {trials.length} clinical trial{trials.length !== 1 ? 's' : ''} · {calendar.filter(e => e.status === 'pending').length} upcoming FDA decision{calendar.filter(e => e.status === 'pending').length !== 1 ? 's' : ''}
                </p>
              </div>
              <a href={`https://www.google.com/search?q=${encodeURIComponent(companyName)}+stock+price`} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, backgroundColor: 'rgba(59,130,246,0.10)', border: `1px solid rgba(59,130,246,0.3)`, color: T.blue, padding: '10px 20px', borderRadius: 9, textDecoration: 'none', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                <StockIcon color={T.blue} size={16}/> View Stock
              </a>
            </div>

            {/* AI Narrative */}
            <div style={{ backgroundColor: T.surface, border: `1px solid rgba(16,185,129,0.2)`, borderLeft: `3px solid ${T.emerald}`, borderRadius: 14, padding: isMobile ? '18px 16px' : '22px 26px', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: `linear-gradient(135deg,rgba(16,185,129,0.3),rgba(16,185,129,0.1))`, border: `1px solid rgba(16,185,129,0.25)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AIIcon color={T.emerald} size={14}/>
                </div>
                <span style={{ fontSize: 11, fontWeight: 800, color: T.emerald, letterSpacing: '1px', textTransform: 'uppercase' }}>AI Intelligence Summary</span>
              </div>
              {narrativeLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: T.emerald, animation: 'pulse 1.4s ease-in-out infinite' }}/>
                  <span style={{ fontSize: 14, color: T.textSub }}>Generating company intelligence...</span>
                </div>
              ) : narrative ? (
                <p style={{ margin: 0, fontSize: isMobile ? 14 : 16, color: '#d0e8ff', lineHeight: 1.8, fontWeight: 400 }}>{narrative}</p>
              ) : (
                <p style={{ margin: 0, fontSize: 14, color: T.textMute }}>Could not generate narrative for this company.</p>
              )}
            </div>

            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'FDA Drugs',        value: drugs.length,   color: T.blue,   icon: <PipelineIcon color={T.blue} size={18}/> },
                { label: 'Clinical Trials',  value: trials.length,  color: T.purple, icon: <TrialIcon color={T.purple} size={18}/> },
                { label: 'Insider Trades',   value: trades.length,  color: T.gold,   icon: <TradeIcon color={T.gold} size={18}/> },
                { label: 'High Risk Items',  value: highRisk,       color: T.red,    icon: <PipelineIcon color={T.red} size={18}/> },
              ].map((s, i) => (
                <div key={i} style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: isMobile ? '14px 16px' : '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 38, height: 38, backgroundColor: `${s.color}12`, border: `1px solid ${s.color}25`, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.icon}</div>
                  <div>
                    <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: s.color, lineHeight: 1, letterSpacing: '-0.5px' }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: T.textMute, marginTop: 3 }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Risk bar */}
            <div style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '18px 22px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textMute, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 12 }}>Risk Profile</div>
              <div style={{ display: 'flex', height: 8, borderRadius: 100, overflow: 'hidden', marginBottom: 12, gap: 2 }}>
                <div style={{ width: pct(totalLow),  backgroundColor: T.green,  borderRadius: 100, transition: 'width 0.5s' }}/>
                <div style={{ width: pct(totalMed),  backgroundColor: T.amber,  borderRadius: 100, transition: 'width 0.5s' }}/>
                <div style={{ width: pct(totalHigh), backgroundColor: T.red,    borderRadius: 100, transition: 'width 0.5s' }}/>
              </div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {[{ label: 'Low', value: totalLow, color: T.green }, { label: 'Medium', value: totalMed, color: T.amber }, { label: 'High', value: totalHigh, color: T.red }].map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: r.color }}/>
                    <span style={{ fontSize: 13, color: T.textSub }}>{r.label}: <span style={{ color: r.color, fontWeight: 700 }}>{r.value}</span></span>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming FDA decisions for this company */}
            {calendar.filter(e => e.status === 'pending').length > 0 && (
              <div style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '18px 22px', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <CalIcon color={T.red} size={16}/>
                  <span style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Upcoming FDA Decisions</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {calendar.filter(e => e.status === 'pending').map((entry, i) => {
                    const days = daysUntil(entry.decision_date);
                    const urgent = days !== null && days >= 0 && days <= 7;
                    const color = urgent ? T.red : days <= 30 ? T.amber : T.blue;
                    return (
                      <div key={i} style={{ backgroundColor: T.surfaceHi, border: `1px solid ${urgent ? 'rgba(239,68,68,0.3)' : T.border}`, borderLeft: `3px solid ${color}`, borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>{entry.drug_name}</div>
                          {entry.indication && <div style={{ fontSize: 13, color: T.textSub }}>{entry.indication.substring(0, 80)}</div>}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>{days === null ? '—' : days < 0 ? 'Past' : days === 0 ? 'TODAY' : `${days}d`}</div>
                          <div style={{ fontSize: 12, color: T.textMute, marginTop: 3 }}>{fmtDate(entry.decision_date)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Insider trades for this company */}
            {trades.length > 0 && (
              <div style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '18px 22px', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <TradeIcon color={T.gold} size={16}/>
                  <span style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Insider Trading Activity</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {trades.slice(0, 8).map((t, i) => {
                    const isBuy = t.trade_type === 'BUY';
                    const color = isBuy ? T.green : T.red;
                    return (
                      <div key={i} style={{ backgroundColor: T.surfaceHi, border: `1px solid ${T.border}`, borderLeft: `3px solid ${color}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                            <span style={{ fontSize: 15, fontWeight: 800, color: T.emerald }}>{t.ticker}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 100, background: isBuy ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color }}>{isBuy ? '▲ BUY' : '▼ SELL'}</span>
                          </div>
                          <div style={{ fontSize: 13, color: T.textSub }}>{t.insider_name} · {t.insider_title?.substring(0, 45)}</div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color }}>{fmt$(t.total_value)}</div>
                          <div style={{ fontSize: 12, color: T.textMute, marginTop: 2 }}>{fmtDate(t.trade_date)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${T.border}`, overflowX: 'auto' }}>
              {[
                { id: 'overview', label: `Overview`                         },
                { id: 'drugs',    label: `FDA Drugs (${drugs.length})`      },
                { id: 'trials',   label: `Clinical Trials (${trials.length})`},
              ].map(tab => {
                const isA = activeTab === tab.id;
                return (
                  <button key={tab.id} onClick={() => { setActiveTab(tab.id); setExpandedCard(null); }}
                    style={{ padding: isMobile ? '10px 14px 9px' : '12px 20px 10px', border: 'none', borderBottom: `2px solid ${isA ? T.emerald : 'transparent'}`, backgroundColor: 'transparent', color: isA ? T.text : T.textSub, cursor: 'pointer', fontSize: isMobile ? 13 : 15, fontWeight: isA ? 700 : 400, transition: 'all 0.15s', marginBottom: -1, whiteSpace: 'nowrap' }}>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Overview tab */}
            {activeTab === 'overview' && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                <div style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <PipelineIcon color={T.blue} size={16}/>
                    <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Top FDA Drugs</span>
                  </div>
                  {drugs.length === 0 ? <div style={{ fontSize: 14, color: T.textMute }}>No FDA drugs found</div> : drugs.slice(0, 6).map((d, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < 5 ? `1px solid ${T.border}` : 'none' }}>
                      <span style={{ fontSize: 14, color: T.textSub, fontWeight: 500 }}>{d.drug_name}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: getRiskColor(d.risk_level), backgroundColor: getRiskBg(d.risk_level), padding: '2px 9px', borderRadius: 100 }}>{d.risk_level}</span>
                    </div>
                  ))}
                </div>
                <div style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <TrialIcon color={T.purple} size={16}/>
                    <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Active Clinical Trials</span>
                  </div>
                  {trials.length === 0 ? <div style={{ fontSize: 14, color: T.textMute }}>No clinical trials found</div> : trials.slice(0, 6).map((t, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < 5 ? `1px solid ${T.border}` : 'none', gap: 8 }}>
                      <span style={{ fontSize: 13, color: T.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{t.title}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: getRiskColor(t.risk_level), backgroundColor: getRiskBg(t.risk_level), padding: '2px 9px', borderRadius: 100, flexShrink: 0 }}>{t.risk_level}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Drugs tab */}
            {activeTab === 'drugs' && (
              <div>
                {drugs.length === 0
                  ? <div style={{ textAlign: 'center', padding: 60, color: T.textMute, fontSize: 16 }}>No FDA drugs found for this company</div>
                  : drugs.map((item, i) => renderDrugCard(item, i))}
              </div>
            )}

            {/* Trials tab */}
            {activeTab === 'trials' && (
              <div>
                {trials.length === 0
                  ? <div style={{ textAlign: 'center', padding: 60, color: T.textMute, fontSize: 16 }}>No clinical trials found for this company</div>
                  : trials.map((item, i) => renderTrialCard(item, i))}
              </div>
            )}

            {/* No data */}
            {!loading && drugs.length === 0 && trials.length === 0 && (
              <div style={{ textAlign: 'center', padding: '80px 40px', backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: 12 }}>
                <div style={{ fontSize: 19, fontWeight: 700, color: T.textSub, marginBottom: 10 }}>No data found for {companyName}</div>
                <div style={{ fontSize: 15, color: T.textMute }}>This company may not have records in our current database</div>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes pulse   { 0%,100%{opacity:1;} 50%{opacity:0.4;} }
        @keyframes loadBar { 0%{transform:translateX(-100%);} 100%{transform:translateX(300%);} }
      `}</style>
    </div>
  );
}